import React from 'react'
import { NumericFormat } from 'react-number-format'
import type { NumberFormatValues } from 'react-number-format/types/types.js'
import { GridCellKind, SelectionRange, type CustomCell, type CustomRenderer } from '@glideapps/glide-data-grid'
import type { Item } from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { RatingCell } from '@/core/models/columns/rating'

interface CustomRatingCellProps {
  readonly type: ColumnTypes
  readonly cell: RatingCell
  readonly rating: number
  readonly min?: number
  readonly max?: number
  readonly extra?: {
    readonly theme: string
  }
}

export type CustomRatingCell = CustomCell<CustomRatingCellProps>

interface Props {
  readonly value: number | undefined
  readonly disabled?: boolean
  readonly onChange: (values: NumberFormatValues) => void
  readonly highlight: boolean
  readonly validatedSelection?: SelectionRange
  readonly fixedDecimals?: number
  readonly allowNegative?: boolean
  readonly thousandSeparator?: boolean | string
  readonly decimalSeparator?: string
  readonly max?: number
  readonly min?: number
  readonly themeMode?: string
}

const NumberOverlayEditor: React.FunctionComponent<Props> = (p) => {
  const {
    value,
    onChange,
    disabled,
    highlight,
    validatedSelection,
    fixedDecimals,
    allowNegative,
    thousandSeparator,
    decimalSeparator,
    max,
    min,
    themeMode,
  } = p
  const inputRef = React.useRef<HTMLInputElement>()

  React.useLayoutEffect(() => {
    if (validatedSelection !== undefined) {
      const range = typeof validatedSelection === 'number' ? [validatedSelection, null] : validatedSelection
      inputRef.current?.setSelectionRange(range[0], range[1])
    }
  }, [validatedSelection])

  return (
    <NumericFormat
      autoFocus={true}
      getInputRef={inputRef}
      className='w-full pt-1'
      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
        e.target.setSelectionRange(highlight ? 0 : e.target.value.length, e.target.value.length)
      }
      disabled={disabled === true}
      decimalScale={fixedDecimals}
      allowNegative={allowNegative}
      thousandSeparator={thousandSeparator ?? ','}
      max={max}
      min={min}
      decimalSeparator={decimalSeparator ?? '.'}
      value={Object.is(value, -0) ? '-' : value ?? ''}
      // decimalScale={3}
      // prefix={"$"}
      onValueChange={onChange}
      style={{
        color: themeMode === 'dark' ? '#fff' : '#1f2937',
        backgroundColor: themeMode === 'dark' ? '#1f2937' : '#fff',
      }}
    />
  )
}

const starPoints = [
  [50, 5],
  [61.23, 39.55],
  [97.55, 39.55],
  [68.16, 60.9],
  [79.39, 95.45],
  [50, 74.1],
  [20.61, 95.45],
  [31.84, 60.9],
  [2.45, 39.55],
  [38.77, 39.55],
]

function pathStar(ctx: CanvasRenderingContext2D, center: Item, size: number, half: boolean = false) {
  let moved = false
  for (let i = 0; i < starPoints.length; i++) {
    const p = starPoints[i]
    const x = (p[0] - 50) * (size / 100) + center[0]
    const y = (p[1] - 50) * (size / 100) + center[1]

    if (half && p[0] > 50) {
      if (i === 0) {
        ctx.lineTo(center[0], y)
        ctx.lineTo(center[0], center[1])
        ctx.closePath()
        break
      }
    } else {
      if (moved) {
        ctx.lineTo(x, y)
      } else {
        ctx.moveTo(x, y)
        moved = true
      }
    }
  }

  if (!half) {
    ctx.closePath()
  }
}

const CustomRatingRenderer: CustomRenderer<CustomRatingCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomRatingCell =>
    (cell.data as CustomRatingCellProps).type === ColumnTypes.Rating,
  draw: async (args, cell) => {
    const ratingCell = cell.data.cell as RatingCell

    drawRating(args, ratingCell)
  },
  provideEditor: (args) => ({
    editor: (p) => {
      const themeMode = args.data.extra?.theme
      const { isHighlighted, onChange, value, validatedSelection } = p
      const ratingMax = value.data.cell.settings.ratingMax
      const ratingMin = value.data.cell.settings.ratingMin
      return (
        <NumberOverlayEditor
          highlight={isHighlighted}
          disabled={value.readonly === true}
          value={value.data.rating}
          fixedDecimals={2}
          allowNegative={false}
          thousandSeparator={','}
          decimalSeparator={'.'}
          validatedSelection={validatedSelection}
          max={ratingMax}
          min={ratingMin}
          themeMode={themeMode}
          onChange={(x) => {
            const minValue = ratingMin ?? 0
            let numValue = Number.isNaN(x.floatValue ?? minValue) ? minValue : x.floatValue ?? minValue

            if (ratingMax !== undefined && ratingMax < numValue) {
              numValue = ratingMax
            }
            if (ratingMin !== undefined && ratingMin > numValue) {
              numValue = ratingMin
            }
            onChange({
              ...value,
              data: {
                type: value.data.type,
                cell: {
                  ...value.data.cell,
                  value: numValue,
                  toCell: (v) => new RatingCell(v, value.data.cell.settings),
                },
                rating: numValue,
              },
            })
          }}
        />
      )
    },
  }),
}

export const drawRating = (args: any, ratingCell: RatingCell) => {
  const { ctx, theme, rect, hoverAmount, cell } = args
  const isHalfIncrement = cell.data.cell.settings.ratingIncrement === 'half'
  const rating = ratingCell.value as number
  const maxStars = ratingCell.settings.ratingMax ?? 5

  const clampedRating = Math.min(rating, maxStars)

  const fullStars = Math.floor(clampedRating)
  const decimal = clampedRating - fullStars
  const hasHalfStar = isHalfIncrement && decimal >= 0.25 && decimal < 0.75

  const adjustedRating = hasHalfStar
    ? fullStars + 0.5
    : decimal >= 0.75
    ? Math.ceil(clampedRating)
    : Math.floor(clampedRating)

  const padX = theme.cellHorizontalPadding
  let drawX = rect.x + padX + 8

  ctx.beginPath()

  for (let i = 0; i < Math.floor(adjustedRating); i++) {
    pathStar(ctx, [drawX, rect.y + rect.height / 2], 16)
    drawX += 18
  }

  if (hasHalfStar) {
    pathStar(ctx, [drawX, rect.y + rect.height / 2], 16, true)
    drawX += 18
  }

  ctx.fillStyle = theme.textDark
  ctx.globalAlpha = 0.6 + 0.4 * hoverAmount
  ctx.fill()
  ctx.globalAlpha = 1

  ctx.strokeStyle = theme.textDark
  ctx.globalAlpha = 0.3 + 0.1 * hoverAmount
  ctx.stroke()
  ctx.globalAlpha = 1
}

export default CustomRatingRenderer
